import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, GraduationCap, Loader2, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { AvatarBuilder, AvatarSVG } from '../components/AvatarBuilder';

const TUTOR_NAMES = ['Buddy', 'Nova', 'Sparky', 'Sage', 'Pixel', 'Atlas', 'Luna', 'Echo'];

const SignupPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1); // 1: details, 2: avatar, 3: tutor name
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', grade_level: 7,
    avatar_skin: '#FFD5A0', avatar_hair: '#4A3728', avatar_eyes: '#4D96FF', avatar_accessory: 'none',
    tutor_name: 'Buddy',
  });
  const [customTutorName, setCustomTutorName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleAvatarChange = (updates) => {
    const mapped = {};
    if (updates.skin) mapped.avatar_skin = updates.skin;
    if (updates.hair) mapped.avatar_hair = updates.hair;
    if (updates.eyes) mapped.avatar_eyes = updates.eyes;
    if (updates.accessory !== undefined) mapped.avatar_accessory = updates.accessory;
    setFormData(prev => ({ ...prev, ...mapped }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    const finalName = customTutorName.trim() || formData.tutor_name;
    const result = await register({ ...formData, tutor_name: finalName, grade_level: parseInt(formData.grade_level) });
    if (result.success) { toast.success(`Welcome to SkillRise! ${finalName} is ready to teach you!`); navigate('/dashboard'); }
    else toast.error(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F8FB] via-[#E8F2FF] to-[#F0E8FF] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 mb-3">
            <img src="/logo.png" alt="SkillRise Logo" className="w-12 h-12 rounded-2xl object-cover shadow-lg" />
            <div>
              <h1 className="text-3xl font-black tracking-tight text-[#1E293B]">SkillRise</h1>
              <p className="text-[10px] font-semibold text-[#64748B] tracking-widest uppercase">by Ekamraj Productions</p>
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex justify-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-2 rounded-full transition-all ${step === s ? 'w-12 bg-[#4D96FF]' : step > s ? 'w-8 bg-[#38E54D]' : 'w-8 bg-slate-200'}`} />
          ))}
        </div>

        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl" data-testid="signup-form">
          {/* Step 1: Account Details */}
          {step === 1 && (
            <>
              <h2 className="text-3xl font-bold text-[#1E293B] mb-6">Create Account</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1E293B] mb-2">Username</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <input data-testid="signup-username-input" type="text" name="username" value={formData.username} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3.5 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10 transition-all"
                      placeholder="Choose a cool username" required minLength={3} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1E293B] mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <input data-testid="signup-email-input" type="email" name="email" value={formData.email} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3.5 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10 transition-all"
                      placeholder="your@email.com" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1E293B] mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <input data-testid="signup-password-input" type="password" name="password" value={formData.password} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3.5 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10 transition-all"
                      placeholder="Min 6 characters" required minLength={6} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1E293B] mb-2">Grade Level</label>
                  <div className="relative">
                    <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <select data-testid="signup-grade-select" name="grade_level" value={formData.grade_level} onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3.5 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] transition-all appearance-none cursor-pointer">
                      <option value={6}>Grade 6</option>
                      <option value={7}>Grade 7</option>
                      <option value={8}>Grade 8</option>
                    </select>
                  </div>
                </div>
                <button onClick={() => { if (!formData.username || !formData.email || !formData.password) { toast.error('Fill all fields'); return; } setStep(2); }} data-testid="next-step-btn"
                  className="w-full py-4 bg-[#4D96FF] hover:bg-[#3B7BE0] text-white rounded-2xl font-bold text-lg border-b-4 border-[#3B7BE0] active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-2">
                  Next: Create Your Avatar <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {/* Step 2: Avatar Builder */}
          {step === 2 && (
            <>
              <h2 className="text-3xl font-bold text-[#1E293B] mb-2">Create Your Avatar!</h2>
              <p className="text-[#64748B] mb-6">Make it look like you! This will be your profile picture.</p>
              <AvatarBuilder skin={formData.avatar_skin} hair={formData.avatar_hair} eyes={formData.avatar_eyes} accessory={formData.avatar_accessory} onChange={handleAvatarChange} />
              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="flex-1 py-4 bg-slate-100 text-[#64748B] rounded-2xl font-bold flex items-center justify-center gap-2">
                  <ChevronLeft className="w-5 h-5" /> Back
                </button>
                <button onClick={() => setStep(3)} data-testid="next-tutor-btn"
                  className="flex-1 py-4 bg-[#FFC03D] text-white rounded-2xl font-bold text-lg border-b-4 border-[#E6A820] active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-2">
                  Next: Name Your Tutor <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {/* Step 3: Name Your Tutor */}
          {step === 3 && (
            <>
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#FFC03D] to-orange-500 rounded-3xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-[#1E293B] mb-2">Name Your AI Tutor!</h2>
                <p className="text-[#64748B]">Your personal tutor will help you learn every chapter with stories and fun explanations.</p>
              </div>

              {/* Preset Names */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {TUTOR_NAMES.map(name => (
                  <button key={name} onClick={() => { setFormData(prev => ({...prev, tutor_name: name})); setCustomTutorName(''); }}
                    data-testid={`tutor-name-${name.toLowerCase()}`}
                    className={`p-3 rounded-xl font-bold text-center transition-all ${formData.tutor_name === name && !customTutorName ? 'bg-[#FFC03D] text-white shadow-lg scale-105' : 'bg-[#F4F8FB] text-[#64748B] border-2 border-slate-200 hover:border-[#FFC03D]'}`}>
                    {name}
                  </button>
                ))}
              </div>

              <div className="text-center text-sm text-[#64748B] font-semibold mb-3">- or type your own name -</div>

              <input type="text" value={customTutorName} onChange={(e) => setCustomTutorName(e.target.value)}
                className="w-full text-center text-xl font-bold p-4 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-[#FFC03D] outline-none mb-6"
                placeholder="Type a custom name..." maxLength={20} data-testid="custom-tutor-name" />

              <div className="bg-[#FFF3D6] rounded-2xl p-4 mb-6 text-center">
                <p className="text-lg font-bold text-[#1E293B]">
                  Your tutor's name: <span className="text-[#FFC03D] text-2xl">{customTutorName.trim() || formData.tutor_name}</span>
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 py-4 bg-slate-100 text-[#64748B] rounded-2xl font-bold flex items-center justify-center gap-2">
                  <ChevronLeft className="w-5 h-5" /> Back
                </button>
                <button onClick={handleSubmit} disabled={loading} data-testid="signup-submit-btn"
                  className="flex-1 py-4 bg-[#38E54D] hover:bg-green-600 text-white rounded-2xl font-bold text-lg border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating...</> : 'Start Learning!'}
                </button>
              </div>
            </>
          )}

          {step === 1 && (
            <div className="mt-6 text-center">
              <p className="text-[#64748B]">Already have an account?{' '}<Link data-testid="signup-to-login-link" to="/login" className="text-[#4D96FF] font-bold hover:underline">Login</Link></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
